'use client';

import { Radio } from '@common/components';
import { cn } from '@core/utils';
import type { DetailHomeGiftProductResponse } from '@generated/unauth';
import { useCallback, useRef } from 'react';
import CountUp from 'react-countup';

import { CartCount } from '@/app/donation/(private)/cart/_components/CartItems/CartCount';

import { formatOptionLabel } from '../../../../_utils/formatOptionLabel';
import { useGiftOption } from '../../_context/GiftOptionContext';
import { AddToCartButton } from '../AddToCartButton';

interface PrefillFormProps {
  productData?: DetailHomeGiftProductResponse;
  productNumber: string;
  className?: string;
}

export const PrefillForm = ({
  productData,
  productNumber,
  className,
}: PrefillFormProps) => {
  const {
    selectedOptions,
    noOptionQuantity,
    handleOptionSelect,
    handleQuantityChange,
    handleOptionDelete,
    setNoOptionQuantity,
    getCartOptions,
  } = useGiftOption();

  const optionGroups = productData?.optionGroupInfoList ?? [];
  const hasOptions = optionGroups.length > 0;

  const basePrice = productData?.requiredDonationAmount ?? 0;

  // 필수 옵션 그룹을 모두 선택했는지 확인
  const requiredOptionGroups = optionGroups.filter((g) => g.requiredYN);
  const isAllRequiredOptionsSelected =
    hasOptions &&
    requiredOptionGroups.every((g) =>
      selectedOptions.has(g.optionGroupId ?? 0),
    );

  // 선택된 옵션 조합 텍스트 생성
  const selectedOptionText = (() => {
    if (!isAllRequiredOptionsSelected) return null;

    const optionNames: string[] = [];
    optionGroups.forEach((group) => {
      const selected = selectedOptions.get(group.optionGroupId ?? 0);
      if (selected) {
        const optionItem = group.optionItemInfoList?.find(
          (item) => item.optionItemId === selected.optionItemId,
        );
        if (optionItem?.itemName) {
          optionNames.push(optionItem.itemName);
        }
      }
    });

    return optionNames.join(' ');
  })();

  // 선택된 옵션의 수량 (첫 번째 옵션 그룹 기준)
  const selectedQuantity = (() => {
    if (!isAllRequiredOptionsSelected || optionGroups.length === 0) return 1;
    const firstGroupId = optionGroups[0]?.optionGroupId ?? 0;

    return selectedOptions.get(firstGroupId)?.quantity ?? 1;
  })();

  const totalPrice = (() => {
    // 옵션 없는 상품
    if (!hasOptions) {
      return basePrice * noOptionQuantity;
    }
    // 옵션 있는 상품 - 선택 안 했을 때
    if (selectedOptions.size === 0) return basePrice;
    // 옵션 있는 상품 - 선택했을 때
    let total = 0;
    let totalExtraPrice = 0;
    selectedOptions.forEach((opt) => {
      totalExtraPrice += opt.extraPrice;
    });
    // 필수 옵션이 모두 선택됐을 때만 수량 적용
    const qty = isAllRequiredOptionsSelected ? selectedQuantity : 1;
    total = (basePrice + totalExtraPrice) * qty;

    return total;
  })();

  // 모든 옵션 그룹의 수량을 동시에 변경
  const handleAllQuantityChange = (quantity: number) => {
    optionGroups.forEach((group) => {
      const groupId = group.optionGroupId ?? 0;
      if (selectedOptions.has(groupId)) {
        handleQuantityChange(groupId, quantity);
      }
    });
  };

  // 모든 선택 옵션 삭제
  const handleAllOptionsDelete = () => {
    optionGroups.forEach((group) => {
      const groupId = group.optionGroupId ?? 0;
      handleOptionDelete(groupId);
    });
  };

  const isSingleOptionGroup = optionGroups.length === 1;
  const requiredOptionGroupIds = optionGroups
    .filter((g) => g.requiredYN)
    .map((g) => g.optionGroupId);

  const optionSectionRef = useRef<HTMLDivElement>(null);
  const scrollToOptionSection = useCallback(() => {
    optionSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, []);

  if (!productData) return null;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className={cn('flex flex-col gap-36', className)}
    >
      <div ref={optionSectionRef} data-gift-option-section className="flex flex-col gap-28">
        {/* 옵션 없는 상품 - 수량 선택 */}
        {!hasOptions && (
          <div className="flex items-center justify-between border-b border-Gray-200 py-16">
            <span className="font-400 text-Gray-700 typo-body4">수량</span>
            <CartCount
              count={noOptionQuantity}
              onCountChange={setNoOptionQuantity}
            />
          </div>
        )}

        {/* 옵션 선택 영역 (라디오 버튼 UI) */}

        {optionGroups.map((optionGroup) => {
          const groupId = optionGroup.optionGroupId ?? 0;
          const selectedInGroup = selectedOptions.get(groupId);

          return (
            <div key={groupId} className="flex flex-col">
              {optionGroup.optionGroupName && (
                <p className="flex items-center gap-4 pb-8 pt-16 font-500 text-Gray-600 typo-body4">
                  {isSingleOptionGroup
                    ? '상품옵션 선택'
                    : optionGroup.optionGroupName}
                  {optionGroup.requiredYN && (
                    <span className="font-600 text-R-500">*</span>
                  )}
                </p>
              )}
              <Radio.Group
                value={selectedInGroup?.optionItemId}
                onChange={(selectedItemId) => {
                  const selectedItem = optionGroup.optionItemInfoList?.find(
                    (item) => item.optionItemId === selectedItemId,
                  );
                  if (selectedItem && !selectedItem.displaySoldOutYN) {
                    handleOptionSelect(
                      groupId,
                      selectedItemId as number,
                      selectedItem.extraPrice ?? 0,
                    );
                  }
                }}
                theme="black"
                className="flex flex-col"
              >
                {optionGroup.optionItemInfoList?.map((optionItem) => {
                  const itemId = optionItem.optionItemId ?? 0;
                  const isSoldOut = optionItem.displaySoldOutYN ?? false;

                  const displayLabel = formatOptionLabel(
                    optionItem.itemName ?? '',
                    { isSoldOut, extraPrice: optionItem.extraPrice },
                  );

                  return (
                    <div
                      key={itemId}
                      className="flex items-center gap-16 border-b border-Gray-200 py-16"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (!isSoldOut) {
                            handleOptionSelect(
                              groupId,
                              itemId,
                              optionItem.extraPrice ?? 0,
                            );
                          }
                        }}
                        disabled={isSoldOut}
                        className={cn(
                          'flex flex-1 items-center',
                          isSoldOut && 'cursor-not-allowed opacity-50',
                        )}
                      >
                        <Radio.Item
                          value={itemId}
                          className="pointer-events-none"
                        >
                          <span className="flex-1 text-left font-400 text-Gray-700 typo-body4">
                            {displayLabel}
                          </span>
                        </Radio.Item>
                      </button>
                    </div>
                  );
                })}
              </Radio.Group>
            </div>
          );
        })}

        {/* 선택된 옵션 표시 영역 */}
        {isAllRequiredOptionsSelected && selectedOptionText && (
          <div className="flex flex-col items-center justify-center rounded-small bg-Gray-25 p-12">
            <div className="flex w-full items-center justify-between">
              <span className="font-400 text-Gray-800 typo-caption1">
                {selectedOptionText} / {selectedQuantity}개
              </span>
              <CartCount
                count={selectedQuantity}
                onCountChange={handleAllQuantityChange}
                onDelete={handleAllOptionsDelete}
              />
            </div>
          </div>
        )}
      </div>

      {/* PC에서만 기부금액 + 버튼 표시 (모바일은 바텀 CTA 사용) */}
      <div className="flex flex-col gap-24 mobile:hidden tablet:hidden">
        <div className="flex items-center justify-between">
          <span className="font-600 text-Gray-900 typo-h6">기부금액</span>
          <span className="flex items-center gap-2 font-800 text-Gray-900 typo-h4">
            <CountUp
              preserveValue
              end={totalPrice}
              duration={0.3}
              delay={0.2}
            />
            <span>원</span>
          </span>
        </div>
        <AddToCartButton
          productInfo={productData}
          productNumber={productNumber}
          selectedOptions={getCartOptions(optionGroups)}
          hasOptions={hasOptions}
          quantity={hasOptions ? selectedQuantity : noOptionQuantity}
          requiredOptionGroupIds={requiredOptionGroupIds}
          theme="primary-fill"
          onOptionNotSelected={scrollToOptionSection}
        />
      </div>
    </form>
  );
};
